import { FeatureFlag } from '@ps/web-core-enums';

import type { WidgetEffectParams } from '@swf/core/store';
import { pageChange } from '@swf/core/utils/actions';
import type { BasicSportsWidgetConfig } from '@swf/sports/pages/types';
import { sportsWidgetPollingBehavior } from '@swf/sports/utils/actions';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';
import getErrorStatusCode from '@swf/sports/utils/getErrorStatusCode';
import { getWidgetCancellationSignal } from '@swf/sports/utils/getWidgetCancellationSignal';
import { hasRequestAborted } from '@swf/sports/utils/hasRequestAborted';

import type { CompetitionEventsSuccessResponse, WidgetDispatch, WidgetSelect } from './types';

export const effects = {
  async preload(_payload: undefined, params: WidgetEffectParams<BasicSportsWidgetConfig>) {
    const { dispatch, rootState, select, widget, actions } = params;
    const { clear, competitionEventsRequested, quickSwitcherRequested }: WidgetDispatch =
      dispatch[widget.uid];
    const { hasPreloaded }: WidgetSelect = select[widget.uid];

    sportsWidgetPollingBehavior(params).subscribe({
      next: (pollingSource) =>
        competitionEventsRequested({ isBackgroundLoading: pollingSource === 'INTERVAL' }),
      complete: () => clear()
    });

    pageChange(actions)?.subscribe({
      next: () => quickSwitcherRequested(),
      complete: () => clear()
    });

    if (!hasPreloaded(rootState)) {
      await competitionEventsRequested();
    }

    quickSwitcherRequested();
  },
  async competitionEventsRequested(
    _payload: undefined,
    params: WidgetEffectParams<BasicSportsWidgetConfig>
  ): Promise<void> {
    const { dispatch, request, rootState, select, widget } = params;
    const { eventTypeId, competitionId } = select.sports.getUrlParams(rootState);

    const { competitionEventsDataLoadFailed, competitionEventsDataLoadSuccess }: WidgetDispatch =
      dispatch[widget.uid];
    const sportsRequest = buildSportsRequest(request, rootState);

    try {
      const {
        body: { data }
      } = await sportsRequest<CompetitionEventsSuccessResponse>({
        path: `/competition-events/${eventTypeId}/${competitionId}`,
        signal: getWidgetCancellationSignal(params),
        includeFeatures: [FeatureFlag.ISPEnableStarSub, FeatureFlag.ISPEnableLiveStreaming]
      });

      competitionEventsDataLoadSuccess(data);
    } catch (error) {
      if (hasRequestAborted(error)) return;

      competitionEventsDataLoadFailed(getErrorStatusCode(error));
    }
  },

  async quickSwitcherRequested(
    _payload: undefined,
    params: WidgetEffectParams<BasicSportsWidgetConfig>
  ) {
    const { dispatch, rootState, select } = params;

    const { competitionId } = select.sports.getUrlParams(rootState);

    dispatch.quickSwitcher.quickSwitcherRequested({
      hierarchyLevel: 'competition',
      facetId: competitionId
    });
  }
};
